import { gaugeBounds } from './mapping';
import type { LiveField, Panel, PanelType } from './types';
import type { TelemetryMetric } from '../types';

export interface PanelDraft {
  type: PanelType;
  title: string;
  metric: string | null;
  serials: string[];
  serial: string | null;
  field: LiveField | null;
  reduce: 'last' | 'avg' | 'max';
  min: number | null;
  max: number | null;
}

export const EMPTY_DRAFT: PanelDraft = {
  type: 'chart', title: '', metric: null, serials: [],
  serial: null, field: null, reduce: 'last', min: null, max: null,
};

export function draftFromPanel(panel: Panel): PanelDraft {
  const base = { ...EMPTY_DRAFT, type: panel.type, title: panel.title };
  switch (panel.type) {
    case 'chart':  return { ...base, metric: panel.metric, serials: panel.serials };
    case 'stat':   return { ...base, metric: panel.metric, serials: panel.serials, reduce: panel.options?.reduce ?? 'last' };
    case 'gauge':  return { ...base, metric: panel.metric, serials: panel.serials, min: panel.options?.min ?? null, max: panel.options?.max ?? null };
    case 'faults': return { ...base, serials: panel.serials };
    case 'live':   return { ...base, serial: panel.serial, field: panel.field };
  }
}

export function validateDraft(draft: PanelDraft, metrics: TelemetryMetric[]): string | null {
  switch (draft.type) {
    case 'chart':
    case 'stat':
    case 'gauge': {
      if (!draft.metric) return 'Pick a metric';
      const def = metrics.find((m) => m.id === draft.metric);
      if (!def) return 'Unknown metric';
      if (def.source !== 'prom') return 'This metric is a log stream — use a Faults panel';
      if (draft.type === 'gauge' && !gaugeBounds(def.unit, { min: draft.min ?? undefined, max: draft.max ?? undefined })) {
        return `Gauge needs an explicit max for unit "${def.unit}"`;
      }
      return null;
    }
    case 'faults':
      return null;
    case 'live': {
      if (!draft.serial) return 'Pick a device';
      if (!draft.field) return 'Pick a field';
      return null;
    }
  }
}

export function draftToPanel(
  draft: PanelDraft,
  id: string,
  pos: { x: number; y: number; w: number; h: number },
  metrics: TelemetryMetric[] = [],
): Panel {
  const title = draft.title.trim()
    || metrics.find((m) => m.id === draft.metric)?.label
    || draft.type;
  const base = { id, title, ...pos };
  switch (draft.type) {
    case 'chart':
      return { ...base, type: 'chart', metric: draft.metric!, serials: draft.serials };
    case 'stat':
      return { ...base, type: 'stat', metric: draft.metric!, serials: draft.serials, ...(draft.reduce !== 'last' ? { options: { reduce: draft.reduce } } : {}) };
    case 'gauge': {
      const options: { min?: number; max?: number } = {};
      if (draft.min !== null) options.min = draft.min;
      if (draft.max !== null) options.max = draft.max;
      return { ...base, type: 'gauge', metric: draft.metric!, serials: draft.serials, ...(Object.keys(options).length ? { options } : {}) };
    }
    case 'faults':
      return { ...base, type: 'faults', serials: draft.serials };
    case 'live':
      return { ...base, type: 'live', serial: draft.serial!, field: draft.field! };
  }
}

export type ConflictResolution =
  | { action: 'reload' }
  | { action: 'overwrite'; retryWith: string };

export function resolveConflict(choice: 'reload' | 'overwrite', freshUpdatedAt: string): ConflictResolution {
  return choice === 'reload'
    ? { action: 'reload' }
    : { action: 'overwrite', retryWith: freshUpdatedAt };
}
